import { Picture } from "@/components/media/Picture";
import { createFileRoute } from "@tanstack/react-router";

import experienceIcon from "@/assets/icons/Experience.png.asset.json";
import giveIcon from "@/assets/icons/Give.png.asset.json";
import impactIcon from "@/assets/icons/Impact.png.asset.json";
import shareTheLoveIcon from "@/assets/icons/ShareTheLove.png.asset.json";
import touchLivesIcon from "@/assets/icons/TouchLives.png.asset.json";

import { TripApplicationForm } from "@/components/forms/TripApplicationForm";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { PageHero } from "@/components/layout/PageHero";
import { UPCOMING_CRUSADES } from "@/data/crusades";
import { heroFor } from "@/data/heroes";

import { IconDiamond } from "@/components/sections/IconDiamond";
import { pageBlocks } from "@/lib/content";

const TITLE = "Join Us — Light of Life International";
const DESCRIPTION =
  "Join a Light of Life International mission team. Serve on a gospel crusade, in orphanages, hospitals and villages across Latin America and Africa.";

export const Route = createFileRoute("/join-us")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://loli.ardent.dev/join-us" },
    ],
    links: [{ rel: "canonical", href: "https://loli.ardent.dev/join-us" }],
  }),
  component: JoinUsPage,
});

const VOLUNTEER_PHOTOS = [
  "/media/vol_01-1.jpg",
  "/media/vol_02-1.jpg",
  "/media/vol_03-1.jpg",
  "/media/vol_04-1.jpg",
  "/media/vol_05-1.jpg",
  "/media/vol_06-1.jpg",
  "/media/vol_07-1.jpg",
];

function JoinUsPage() {
  const blocks = pageBlocks("join-us");
  const text = (index: number) => {
    const block = blocks[index];
    return block && "text" in block ? block.text : "";
  };

  const ways = [
    { src: shareTheLoveIcon.url, label: text(7) },
    { src: giveIcon.url, label: text(8) },
    { src: experienceIcon.url, label: text(9) },
    { src: touchLivesIcon.url, label: text(10) },
    { src: impactIcon.url, label: text(11) },
  ];

  return (
    <div className="flex min-h-dvh flex-col">
      <Header overlay />
      <main id="main" className="flex-1">
        <PageHero title="Join Us" image={heroFor("/join-us").image} subtitle={text(1)} />

        {/* Share the love of Jesus in — trip highlight */}
        <section className="section-y bg-background">
          <div className="container-site">
            <h2 className="text-center text-4xl text-foreground sm:text-5xl">
              Share the love of Jesus in
            </h2>

            <ul className="mt-14 grid gap-6 md:grid-cols-2">
              {UPCOMING_CRUSADES.map((crusade) => (
                <li key={crusade.city}>
                  <a
                    href="#jointeam"
                    className="relative block aspect-[4/3] overflow-hidden rounded-xl"
                  >
                    <Picture
                      src={crusade.image}
                      alt={`Crowd worshipping at a Light of Life International crusade in ${crusade.city}, ${crusade.country}`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover"
                      sizes="100vw"
                    />
                    <span className="absolute inset-0 bg-navy/45" aria-hidden />
                    <span className="relative flex h-full flex-col items-center justify-center px-6 text-center">
                      <span className="block text-3xl leading-none text-white uppercase sm:text-4xl">
                        {crusade.city}
                      </span>
                      <span className="mt-1 block text-4xl leading-none text-white uppercase sm:text-5xl">
                        {crusade.country}
                      </span>
                      <span className="eyebrow mt-4 block text-sm text-white">{crusade.dates}</span>
                      <span className="mt-6 inline-block rounded-md border-2 border-white px-6 py-2 font-sans font-semibold text-white transition hover:bg-white hover:text-navy">
                        Sign Up
                      </span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-14 text-center">
              <p className="mx-auto max-w-2xl text-foreground">{text(1)}</p>
              <a
                href="#jointeam"
                className="mt-8 inline-block rounded-md bg-navy px-8 py-4 font-sans font-semibold text-white transition hover:bg-navy/90"
              >
                Join The Team
              </a>
            </div>

            {/* Ways you will serve */}
            <ul className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
              {ways.map((way) => (
                <li
                  key={way.label}
                  className="flex flex-col items-center rounded-2xl bg-muted px-6 py-10 text-center"
                >
                  <IconDiamond src={way.src} />
                  <h3 className="mt-6 text-xl leading-tight font-semibold text-brand-ink">
                    {way.label}
                  </h3>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Application form */}
        <section
          id="jointeam"
          className="section-y scroll-mt-24 rounded-t-[2.5rem] bg-navy text-white"
        >
          <div className="container-site">
            <h2 className="text-center text-4xl text-white/70 sm:text-5xl">
              Join The <span className="text-accent">Team</span>
            </h2>
            <TripApplicationForm />
            <p className="mt-8 font-sans text-sm text-white/80">{text(19)}</p>
          </div>
        </section>

        {/* Prayer network */}
        <section className="section-y -mt-10 rounded-t-[2.5rem] bg-background">
          <div className="container-site grid items-center gap-10 md:grid-cols-2">
            <h2 className="text-4xl text-muted-foreground sm:text-5xl">
              Join the Light of Life International{" "}
              <span className="text-brand-ink">Prayer Network</span>
            </h2>
            <div>
              <p className="text-foreground">{text(22)}</p>
              <a
                href="/contact"
                className="mt-8 inline-block rounded-md bg-accent px-8 py-4 font-sans font-semibold text-navy transition hover:brightness-110"
              >
                Connect With Us
              </a>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-3 px-3 sm:grid-cols-4 lg:grid-cols-7">
            {VOLUNTEER_PHOTOS.map((src) => (
              <Picture
                key={src}
                src={src}
                alt="Light of Life International volunteers serving on a mission trip"
                loading="lazy"
                decoding="async"
                className="h-56 w-full rounded-md object-cover"
                sizes="(max-width: 768px) 50vw, 14vw"
                widths={[640]}

              />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
